'use client';

import { ReactNode, useState } from 'react';
import {
  Box,
  Collapse,
  type CollapseProps,
  Stack,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material';

import mixinSx from '@watershed/style/mixinSx';

import CollapseButton from './CollapseButton';
import { CollapseIconProps } from './CollapseIcon';

export default function CollapsibleBlock({
  title,
  initiallyExpanded,
  children,
  expanded,
  onToggle = () => {},
  sx,
  isIconOnRight,
  buttonSx,
  iconProps,
  typographyProps = {},
  rightComponent,
  childrenContainerSx = {},
  collapseProps,
}: {
  title: LocalizedString | JSX.Element;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  initiallyExpanded?: boolean;
  isIconOnRight?: boolean;
  children: ReactNode;
  sx?: SxProps<Theme>;
  buttonSx?: SxProps<Theme>;
  iconProps?: CollapseIconProps;
  typographyProps?: TypographyProps<'span'>;
  rightComponent?: ReactNode;
  childrenContainerSx?: SxProps<Theme>;
  collapseProps?: Omit<CollapseProps, 'in' | 'children'>;
}) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded ?? true);

  const isControlled = expanded !== undefined;
  const handleToggle = (nextExpanded: boolean) => {
    if (!isControlled) {
      setIsExpanded(nextExpanded);
    }
    onToggle(nextExpanded);
  };

  return (
    <Box sx={sx}>
      <CollapseButton
        isExpanded={isControlled ? expanded : isExpanded}
        setIsExpanded={handleToggle}
        isIconOnRight={isIconOnRight}
        sx={mixinSx(
          {
            width: `100%`,
            flexGrow: 1,
            alignItems: 'center',
            justifyContent: isIconOnRight ? 'flex-end' : 'flex-start',
          },
          buttonSx
        )}
        typographyProps={{
          ...typographyProps,
        }}
        iconProps={iconProps}
      >
        <Stack direction="row" gap={1} alignItems="center">
          {title}
          {/* Add a flexGrow box to make these slide to fill the space. */}
          {rightComponent && <Box sx={{ flexGrow: 1 }} />}
          {rightComponent}
        </Stack>
      </CollapseButton>

      <Collapse
        {...collapseProps}
        in={isControlled ? expanded : isExpanded}
        sx={mixinSx({ gridColumnStart: 2 }, collapseProps?.sx)}
      >
        <Box sx={mixinSx({ paddingY: 1 }, childrenContainerSx)}>{children}</Box>
      </Collapse>
    </Box>
  );
}
